import React, { ChangeEvent, FC, useEffect, useState } from 'react'
import clsx from 'clsx'
import { sumBy } from 'lodash'

// Components
import { FurtherRemarksInput } from '../Operations/FurtherRemarksInput/FurtherRemarksInput'
import { JudgesSignature } from '../Operations/JudgesSignature/JudgesSignature'
import SwitchElement from '../../../elements/toggles/toggles/SwitchElement'

// Helpers
import { toFixed } from '../../../../helpers/helpers'
import { handleImageError } from '../../../../helpers/handleImageError'
import { cn } from '../../../../lib/utils'
import useToasterHelper from '../../../../helpers/ToasterHelper'

import { selectProfileData } from '../../../../store/user/userSlice'
import { useAppSelector } from '../../../../store/hooks'

import {
  IJudge,
  IRegistrationByDayInterface,
} from '../../../../models/registrations-by-day/registrationByDay.interface'

import styles from './JudgeItem.module.css'

import { CONST } from '../../../../const/const'
import { MESSAGES_CONST } from '../../../../const/messages-const'

interface JudgeItemProps {
  judge: IJudge
  judgeIndex: number
  row: IRegistrationByDayInterface
  setRow: (value: IRegistrationByDayInterface) => void
  editable: boolean
  maxPoints: number
  isLocked: boolean
  showLockedModal: () => void
}

export const JudgeItem: FC<JudgeItemProps> = ({
  judge,
  judgeIndex,
  row,
  setRow,
  editable,
  maxPoints,
  isLocked,
  showLockedModal,
}) => {
  const toastFunctions = useToasterHelper()

  const profileData = useAppSelector(selectProfileData)

  const [isOpen, setIsOpen] = useState(false)
  const [totalJudgePoints, setTotalJudgePoints] = useState(0)
  const [isRemarks, setIsRemarks] = useState(false)

  useEffect(() => {
    if (row.score?.judges && row.score.judges[judgeIndex]?.sections?.length > 0) {
      let totalPoints_ = 0

      row.score.judges[judgeIndex].sections.forEach((section) =>
        section.rows.forEach((row) => {
          if (row.coefficient) {
            totalPoints_ += Number(row.coefficient) * Number(row.score)
          } else if (row.fraction === 'minus') {
            totalPoints_ -= Number(row.score)
          } else {
            totalPoints_ += Number(row.score)
          }
        })
      )

      totalPoints_ -= row.score.judges[judgeIndex].errors || 0

      setTotalJudgePoints(totalPoints_)
    } else {
      setTotalJudgePoints(0)
    }
  }, [judge.sections, judgeIndex, row])

  const onChangeFurtherRemarks = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (isLocked) {
      showLockedModal()
    } else if (row.score?.judges) {
      row.score.judges[judgeIndex].furtherRemarks = e.target.value
      setRow({ ...row })
    }
  }

  return (
    <div className={clsx('p-1 border border-SeabiscuitLightThemeColorD3 rounded-md', styles.judge)}>
      <div className="flex items-center gap-2">
        <img
          onClick={() => row.score?.sheet?.name && setIsOpen(!isOpen)}
          className={cn(
            'w-[45px] h-[45px] object-cover rounded-md cursor-pointer transition-all',
            row.score?.sheet?.name && 'hover:opacity-70'
          )}
          src={judge.picture}
          onError={(e) => handleImageError(e, judge.name)}
          alt=""
        />
        <div
          className={cn(
            'flex-1 cursor-pointer transition-all',
            row.score?.sheet?.name && 'hover:opacity-70'
          )}
          onClick={() => row.score?.sheet?.name && setIsOpen(!isOpen)}
        >
          <p className="text-[14px] text-SeabiscuitDark200ThemeColor/50">Judge {judgeIndex + 1}</p>
          {judge.name}{' '}
          {editable && judge.signatureDate && <span className="opacity-50">• Signed</span>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-2">
            <p className="text-base text-SeabiscuitDark200ThemeColor/50">Remarks</p>
            <SwitchElement
              on={isRemarks}
              buttonClassName="small"
              onSwitch={() => {
                if (
                  profileData.id === row.userId ||
                  profileData.id === row.riderId ||
                  profileData.userType === CONST.USE_MODE.ORGANIZER ||
                  editable
                ) {
                  setIsRemarks(!isRemarks)
                } else {
                  toastFunctions.info({ message: MESSAGES_CONST.NOT_REGISTERED })
                }
              }}
            />
          </div>
          <p>
            {totalJudgePoints} / {maxPoints}
          </p>
          <p className="bg-SeabiscuitGrayThemeColor rounded-full p-2 min-w-[72px] text-center">
            {toFixed((totalJudgePoints / maxPoints) * 100, 3) || 0}%
          </p>
        </div>
      </div>
      {isOpen && (
        <div className="mt-1 px-2 border-t border-SeabiscuitLightThemeColorD3">
          {judge.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mt-4 mb-4">
              {sectionIndex !== 0 && <hr />}
              <div className="my-4 flex justify-between items-center">
                <h4 className="text-[20px]">{section.name}</h4>
                <p className="bg-SeabiscuitGrayThemeColor rounded-full p-2 min-w-[102px] text-center">
                  Max: {section.max}
                </p>
              </div>
              <div className="flex gap-2">
                {section.type === 'test' ? (
                  <>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 w-[62px]">
                      Mvmt.
                    </div>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 flex-1">
                      Test
                    </div>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 w-[62px]">
                      Score
                    </div>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 w-[62px]">
                      Coeffic.
                    </div>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 w-[62px]">
                      Final Sc.
                    </div>
                  </>
                ) : section.type === 'score' ? (
                  <>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 w-[62px]">
                      Mvmt.
                    </div>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 flex-1">
                      Test
                    </div>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 w-[62px]">
                      Score
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 flex-1">
                      Mark
                    </div>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 w-[62px]">
                      Score
                    </div>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 w-[62px]">
                      Coeffic.
                    </div>
                    <div className="text-[14px] text-SeabiscuitDark200ThemeColor/50 w-[62px]">
                      Final Sc.
                    </div>
                  </>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {section.rows.map((item, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    <div className={clsx('flex gap-2 min-h-[62px]', styles.item)}>
                      {Number(item.number) >= 0 && (
                        <div className="p-2 w-[62px] bg-SeabiscuitGrayThemeColor rounded-md flex items-center justify-center font-bold">
                          {item.number}
                        </div>
                      )}
                      <div
                        className="p-2 flex-1 bg-SeabiscuitGrayThemeColor rounded-md flex flex-col justify-center"
                        dangerouslySetInnerHTML={{ __html: item.description }}
                      />
                      <input
                        disabled={!editable}
                        className={clsx(
                          'placeholder:text-base text-base p-2 border rounded-md flex items-center justify-center text-center',
                          item.coefficient || section.type === 'score'
                            ? 'w-[62px]'
                            : 'w-[184px] font-bold',
                          Number(item.score) || !editable
                            ? item.fraction === 'minus'
                              ? 'border-none text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5'
                              : 'border-SeabiscuitGrayThemeColor bg-SeabiscuitGrayThemeColor'
                            : 'border-SeabiscuitLightThemeColorD3 bg-white'
                        )}
                        type="number"
                        placeholder="0"
                        value={item.score}
                        onChange={(e) => {
                          if (isLocked) {
                            showLockedModal()
                          } else if (row.score?.judges && editable) {
                            row.score.judges[judgeIndex].sections[sectionIndex].rows[
                              rowIndex
                            ].score = e.target.value
                            setRow({ ...row })
                          }
                        }}
                        onWheel={(e: any) => e.target.blur()}
                      />
                      {item.coefficient && (
                        <>
                          <div
                            className={clsx(
                              'p-2 w-[62px] border rounded-md flex items-center justify-center',
                              Number(item.score) || !editable
                                ? 'border-SeabiscuitGrayThemeColor bg-SeabiscuitGrayThemeColor text-SeabiscuitDark200ThemeColor'
                                : 'border-SeabiscuitLightThemeColorD3 bg-white text-SeabiscuitDark200ThemeColor/50'
                            )}
                          >
                            {item.coefficient}
                          </div>
                          <div
                            className={clsx(
                              'overflow-auto p-2 w-[62px] border rounded-md flex items-center justify-center font-bold',
                              Number(item.score) || !editable
                                ? 'bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor border-SeabiscuitMainThemeColor/5'
                                : 'text-SeabiscuitDark200ThemeColor/50 border-SeabiscuitLightThemeColorD3'
                            )}
                          >
                            {item.coefficient * Number(item.score)}
                          </div>
                        </>
                      )}
                    </div>
                    {isRemarks && (
                      <input
                        disabled={!editable}
                        className={clsx(
                          'p-3 min-h-[62px] w-full border border-SeabiscuitLightThemeColorD3 rounded-md',
                          item.remarks || !editable ? 'bg-SeabiscuitGrayThemeColor' : 'bg-white'
                        )}
                        type="text"
                        placeholder={editable ? 'Enter remarks...' : 'No remarks'}
                        value={item.remarks}
                        onChange={(e) => {
                          if (isLocked) {
                            showLockedModal()
                          } else if (row.score?.judges && editable) {
                            row.score.judges[judgeIndex].sections[sectionIndex].rows[
                              rowIndex
                            ].remarks = e.target.value
                            setRow({ ...row })
                          }
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div>
                {section.notice && (
                  <div className="mt-2 flex flex-col justify-center p-4 bg-SeabiscuitGrayThemeColor min-h-[62px] text-[14px] rounded-md">
                    {section.notice}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="my-4 pt-4 border-t border-SeabiscuitLightThemeColorD3 flex flex-col gap-2">
            {judge.sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className={clsx('flex gap-2 min-h-[62px]', styles.item)}>
                <div className="p-2 flex-1 bg-SeabiscuitGrayThemeColor rounded-md flex flex-col justify-center">
                  <h4 className="font-bold">{section.name}</h4>
                  <p>Max {section.max}</p>
                </div>
                <div className="flex flex-col text-center justify-center items-center p-2 w-3/12 bg-SeabiscuitGrayThemeColor rounded-md font-bold">
                  {toFixed(
                    sumBy(section.rows, (row) =>
                      row.coefficient
                        ? Number(row.coefficient) * Number(row.score)
                        : row.fraction === 'minus'
                          ? -Number(row.score)
                          : Number(row.score)
                    ),
                    3
                  )}
                </div>
              </div>
            ))}
            {row.score?.sheet?.type === 'score-sheet-1' && (
              <div className={clsx('flex gap-2 min-h-[62px]', styles.item)}>
                <div className="p-2 flex-1 bg-SeabiscuitGrayThemeColor rounded-md flex flex-col justify-center">
                  <h4 className="font-bold">Errors</h4>
                  <p>1st time = 2pts • 2nd time = 4pts • 3rd time = Elimination</p>
                </div>
                <div className="flex flex-col text-center justify-center items-center w-3/12 rounded-md">
                  <input
                    disabled={!editable}
                    className={clsx(
                      'font-bold w-full h-full p-2 border rounded-md flex items-center justify-center text-center',
                      Number(judge.errors) || !editable
                        ? 'border-none text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5'
                        : 'border-SeabiscuitLightThemeColorD3 bg-white'
                    )}
                    type="number"
                    placeholder="0"
                    value={Number(judge.errors) > 0 ? judge.errors : ''}
                    onChange={(e) => {
                      if (isLocked) {
                        showLockedModal()
                      } else if (row.score?.judges && editable) {
                        row.score.judges[judgeIndex].errors = Number(e.target.value)
                        setRow({ ...row })
                      }
                    }}
                    onWheel={(e: any) => e.target.blur()}
                  />
                </div>
              </div>
            )}
            <div className={clsx('flex gap-2 min-h-[62px]', styles.item)}>
              <div className="p-2 flex-1 bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor rounded-md flex flex-col justify-center">
                <h4 className="font-bold">Total Score</h4>
                <p>
                  {judge.sections.map((section) => section.name).join(' + ')} • Max {maxPoints}
                </p>
              </div>
              <div className="flex gap-2 text-center justify-center items-center p-2 w-3/12 bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor rounded-md font-bold">
                <span className="font-bold">{toFixed(totalJudgePoints, 3)}</span>•
                <span>{toFixed((totalJudgePoints / maxPoints) * 100, 3)}%</span>
              </div>
            </div>
          </div>
          {isRemarks &&
            (profileData.id === row.riderId ||
              profileData.id === row.userId ||
              profileData.userType === CONST.USE_MODE.ORGANIZER) && (
              <FurtherRemarksInput
                editable={editable}
                furtherRemarksValue={judge.furtherRemarks}
                onChangeFurtherRemarks={onChangeFurtherRemarks}
              />
            )}
          {editable && (
            <JudgesSignature
              isLocked={isLocked}
              showLockedModal={showLockedModal}
              row={row}
              setRow={setRow}
              judgeIndex={judgeIndex}
            />
          )}
        </div>
      )}
    </div>
  )
}
